import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CaseEvent from 'src/components/SupportCase/CaseEvent';
import {
  CaseFiles,
  CaseSchedules,
} from 'src/components/SupportCase/CaseSections';
import CaseHeader from 'src/components/SupportCase/CaseHeader';
import type { CaseHeaderVariant } from 'src/components/SupportCase/CaseHeader';
import Composer from 'src/components/SupportCase/Composer';
import PromptFooter from 'src/components/SupportCase/PromptFooter';
import { useCaseStream } from 'src/components/SupportCase/useCaseStream';
import type {
  AgentHealth,
  CaseEvent as CaseEventType,
  CaseState,
  CaseSummary,
  SavePromptOffer,
} from 'src/components/SupportCase/types';

interface CaseStreamProps {
  caseId: number | null;
  health: AgentHealth | null;
  healthLoading?: boolean;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  onClose?: () => void;
  /**
   * `stacked` is the floating panel's two-row header (ENG-4299); the case page
   * keeps `inline`, where the single row has room.
   */
  headerVariant?: CaseHeaderVariant;
  onCaseChange?: (summary: CaseSummary) => void;
  /**
   * The save offer the hub carried over from the composer it navigated away from
   * (ENG-4220). Rendered under this case's composer so the offer survives the
   * navigation that creating the case causes.
   */
  promptOffer?: SavePromptOffer | null;
}

const BUSY_STATES = new Set<CaseState>(['agent_active']);

/**
 * The case panel (ENG-4137, decision 4A).
 *
 * Not a modal. A support question is usually asked *about* something on the screen,
 * so the panel sits beside the work and the page underneath stays usable and
 * scrollable. That is also why the fullscreen toggle exists rather than a dialog:
 * the same surface, bigger.
 *
 * The panel owns no state of its own beyond presentation: the stream hook holds the
 * events, the cursor and the socket, so the full-page route and the floating panel
 * show the same case the same way.
 */
const CaseStream: FC<CaseStreamProps> = ({
  caseId,
  health,
  healthLoading = false,
  fullscreen = false,
  onToggleFullscreen,
  onClose,
  headerVariant = 'inline',
  onCaseChange,
  promptOffer = null,
}) => {
  const {
    detail,
    events,
    streaming,
    isConnected,
    isLoading,
    error,
    send,
    approve,
    refetch,
  } = useCaseStream({ caseId });

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const summary = detail?.case ?? null;
  const state = summary?.state ?? 'agent_active';

  useEffect(() => {
    if (summary && onCaseChange) {
      onCaseChange(summary);
    }
  }, [summary, onCaseChange]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [events.length, streaming]);

  /** Approval ids that already have a decision recorded later in the timeline. */
  const decidedApprovals = useMemo(() => {
    const decided = new Set<string>();
    events.forEach((event: CaseEventType) => {
      if (
        event.event_type === 'approval_granted' ||
        event.event_type === 'approval_declined' ||
        event.event_type === 'approval_expired'
      ) {
        const id = event.payload?.approval_id;
        if (typeof id === 'string') {
          decided.add(id);
        }
      }
    });
    return decided;
  }, [events]);

  /**
   * The files still in the case, by id (ENG-4185).
   *
   * A sent message names its files from the event payload, which survives the file.
   * This set is how the chip knows whether the stored copy is still there, so a
   * removed file reads "removed" instead of offering a link that 404s.
   *
   * PA-06 — `undefined` while the detail has not loaded, never an empty set. An empty
   * set means "every file was removed", so a panel that opened a moment ago labelled
   * a live attachment `removed` and gave it no link: the file was on the case page and
   * not in the panel. Unknown and removed are different answers.
   */
  const availableDocumentIds = useMemo(
    () =>
      detail
        ? new Set((detail.documents ?? []).map((document) => document.id))
        : undefined,
    [detail]
  );

  const handleDecide = useCallback(
    (approvalId: string, decision: 'confirm' | 'decline') => {
      void approve(approvalId, decision);
    },
    [approve]
  );

  const busy = BUSY_STATES.has(state);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: 'background.paper',
      }}
      data-testid="case-stream"
      aria-label="Pronto Assistant case"
    >
      {/* ENG-4299 — the header is its own component: the panel needs two rows and
          the case page keeps one, and both must render the same references. */}
      <CaseHeader
        title={summary?.title || 'Pronto Assistant'}
        state={state}
        jiraState={summary?.jira_state}
        certification={summary?.certification ?? null}
        property={summary?.property ?? null}
        jiraKey={summary?.jira?.key ?? ''}
        isConnected={isConnected}
        fullscreen={fullscreen}
        onToggleFullscreen={onToggleFullscreen}
        onClose={onClose}
        variant={headerVariant}
      />

      <Divider />

      {detail ? (
        <Box sx={{ px: 1 }}>
          <CaseFiles documents={detail.documents ?? []} onRemoved={refetch} />
          <CaseSchedules
            enabled={Boolean(detail.capabilities?.supports_schedules)}
            caseId={caseId}
          />
        </Box>
      ) : null}

      <Divider />

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
          pb: 10,
          minHeight: 0,
        }}
      >
        <Stack spacing={2}>
          {isLoading && !detail ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {!isLoading && events.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Ask anything — what is missing, why it is not ready, or what needs
              to change.
            </Typography>
          ) : null}

          {events.map((event) => (
            <CaseEvent
              key={event.id}
              event={event}
              decided={
                typeof event.payload?.approval_id === 'string' &&
                decidedApprovals.has(event.payload.approval_id as string)
              }
              busy={busy}
              onDecide={handleDecide}
              availableDocumentIds={availableDocumentIds}
            />
          ))}

          {streaming ? (
            <Stack spacing={0.5}>
              <Chip label="Pronto Assistant" size="small" variant="outlined" />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {streaming}
              </Typography>
            </Stack>
          ) : null}

          {busy && !streaming ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Pronto Assistant is working…
              </Typography>
            </Stack>
          ) : null}

          <div ref={bottomRef} />
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.5 }}>
        {/*
          A resolved case is not a dead end: replying reopens it (the server does that
          in `CaseMessageView`) until the 7-day window closes. The composer used to be
          disabled here, which left the user looking at a closed conversation with no
          way back in and a placeholder claiming the assistant was working.
        */}
        <Composer
          onSend={send}
          disabled={busy}
          health={health}
          healthLoading={healthLoading}
          footer={<PromptFooter offer={promptOffer} />}
        />
      </Box>
    </Box>
  );
};

export default CaseStream;
